import { useState } from "react";
import { useTranslation } from "react-i18next";

import AddItemDialog from "../../../../components/editableItemList/addItemDialog/AddItemDialog";
import { useNewOrderQueryData } from "../../../../hooks/useNewOrderQueryData";
import { IDeliveryItem } from "../../../../models/deliveryItem";
import { getLowestAvailablePosition } from "../../../../utils/helper";
import AddDeliveryItemForm from "../forms/addDeliveryItemForm/AddDeliveryItemForm";

interface AddDeliveryItemDialogProps {
  isOpen: boolean;
  existingPositions: number[];
  closeDialog: () => void;
  onSave: (item: IDeliveryItem) => void;
  itemToEdit?: IDeliveryItem | null;
}

const emptyFormData = (positions: number[]): IDeliveryItem => {
  return {
    id: "",
    position: getLowestAvailablePosition(positions),
    material: "",
    materialId: "",
    quantity: 1,
    unitOfMeasurement: "",
    stockType: "",
    stockTypeId: "",
    itemCategory: "",
    batch: "",
    owner: "",
    serialNumbers: "",
  };
};

const isKeyOfFormData = (key: string): key is keyof IDeliveryItem => {
  return key in emptyFormData([]);
};

const AddDeliveryItemDialog = (props: AddDeliveryItemDialogProps) => {
  const { t } = useTranslation();
  const { queryData } = useNewOrderQueryData();

  const { isOpen, existingPositions, closeDialog, onSave, itemToEdit } = props;

  const [formData, setFormData] = useState<IDeliveryItem>(
    itemToEdit || emptyFormData(existingPositions),
  );
  const [isFormValid, setIsFormValid] = useState(false);

  const handleMaterialChange = (value: string) => {
    if (value === "") {
      // we need to reset the values of the fields whose options depend on the material field
      setFormData({
        ...formData,
        material: "",
        materialId: "",
        unitOfMeasurement: "",
        batch: "",
      });
    } else {
      // we need to set the material field and the materialId field
      setFormData({
        ...formData,
        material: value,
        materialId:
          queryData.materials?.find((material) => material.name === value)
            ?.id || "",
      });
    }
  };

  const handleStockTypeChange = (value: string) => {
    if (value === "") {
      // we need to reset the values of the fields whose options depend on the stockType field
      setFormData({
        ...formData,
        stockType: "",
        stockTypeId: "",
      });
    } else {
      // we need to set the material field and the stockTypeId field
      setFormData({
        ...formData,
        stockType: value,
        stockTypeId:
          queryData.stockTypes?.find(
            (stockType) => stockType.longName === value,
          )?.id || "",
      });
    }
  };

  const onChange = (fieldId: string, value: string) => {
    if (!isKeyOfFormData(fieldId)) {
      console.error(`Invalid fieldId of input: ${fieldId}`);
      return;
    }

    if (fieldId === "material") {
      // we need to handle setting the material field differently
      handleMaterialChange(value);
      return;
    }

    if (fieldId === "stockType") {
      // we need to handle setting the material field differently
      handleStockTypeChange(value);
      return;
    }

    setFormData({ ...formData, [fieldId]: value });
  };

  return (
    <AddItemDialog
      isOpen={isOpen}
      title={t("addNewItem")}
      formData={formData}
      isFormValid={isFormValid}
      closeDialog={closeDialog}
      onSave={onSave}
      itemToEdit={itemToEdit}
    >
      <AddDeliveryItemForm
        formData={formData}
        onChange={onChange}
        setIsFormValid={(value) => setIsFormValid(value)}
        existingPositions={existingPositions}
      />
    </AddItemDialog>
  );
};

export default AddDeliveryItemDialog;
